/**
 * JsonColumnToggleCell — thin gating wrapper that decides whether the
 * Table Designer column-property panel should surface the `JsonColumnToggle`
 * for a given column row.
 *
 * Keeps the eligibility predicate + engine normalization out of the
 * already-large `ColumnsTab.tsx` (additive-mount discipline). Renders nothing
 * for non-string / non-eligible columns, dropped rows, or parsed-only editors
 * (no live connection).
 *
 * Plan: § Phase 7 — Table Designer "Treat as JSON column" toggle.
 */
import { JsonColumnToggle } from './JsonColumnToggle';
import {
  categorizeJsonColumn,
  isNativeJsonColumn,
} from '../../common/utils/jsonColumnCategorization';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

export type JsonColumnToggleCellProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly table: string;
  readonly column: string;
  /** Column's engine type string (e.g. `nvarchar(max)`). */
  readonly dataType: string | null;
  readonly engine: DatabaseEngineId;
  /** When true the column row is being dropped — no toggle. */
  readonly dropped?: boolean;
  readonly testId?: string;
};

export function JsonColumnToggleCell({
  connectionId,
  database,
  schema,
  table,
  column,
  dataType,
  engine,
  dropped = false,
  testId = 'json-column-toggle',
}: JsonColumnToggleCellProps) {
  if (dropped || !dataType || dataType === '—') return null;

  // Type-eligibility only (declared-state is irrelevant to *whether* the
  // affordance shows). `null` metaInfo keeps native/eligible classification;
  // 'not-json' columns (int, etc.) get no toggle.
  const input = { engine, schema, table, name: column, dataType };
  const category = categorizeJsonColumn(input, null);
  if (category === 'not-json') return null;
  // Native JSON columns (PG json/jsonb, MySQL/MSSQL/Oracle json) are already
  // JSON — "Treat as JSON column" is a *string-column* affordance, so an OFF
  // toggle here would just invite a redundant declaration. Hide it.
  if (isNativeJsonColumn(input)) return null;

  return (
    <div className="mt-1.5">
      <JsonColumnToggle
        connectionId={connectionId}
        database={database}
        schema={schema}
        table={table}
        column={column}
        dataType={dataType}
        engine={engine}
        testId={testId}
      />
    </div>
  );
}
